import { useState, useEffect } from 'react';
import { YStack, ScrollView } from 'tamagui';
import { IS_DEMO, DEMO_CREDENTIALS } from '../../config';
import { useAuth } from '../../auth/useAuth';
import './auth-form.css';

interface LoginProps {
  onLogin?: (email: string, password: string) => void;
  onNavigateToSignup?: () => void;
  onLoginSuccess?: () => void;
}

export const Login = ({ onLogin, onNavigateToSignup, onLoginSuccess }: LoginProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const { login, isLoading, user } = useAuth();

  // Redirect on successful login
  useEffect(() => {
    if (user && !isLoading) {
      console.log('User logged in:', user.email);
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    }
  }, [user, isLoading, onLoginSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    
    setLoginError(null);
    setLocalLoading(true);
    
    try {
      console.log('Attempting login with email:', email);
      if (onLogin) {
        await onLogin(email, password);
      } else {
        await login(email, password);
        console.log('Login successful');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed. Please try again.';
      console.error('Login error:', err);
      setLoginError(errorMessage);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleDemoLogin = (type: 'user' | 'admin') => {
    const creds = DEMO_CREDENTIALS[type];
    setEmail(creds.username);
    setPassword(creds.password);
  };

  return (
    <ScrollView flex={1} backgroundColor="$background">
      <YStack className="auth-container">
        <div className="auth-wrapper">
          <div className="auth-card">
            {/* Header */}
            <div className="auth-header">
              <h1 className="auth-title">Welcome Back</h1>
              <p className="auth-subtitle">Sign in to your account to continue</p>
            </div>

            {/* Alert Messages */}
            {loginError && (
              <div className="auth-alert auth-alert-error">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="2"/>
                  <path d="M10 6V10M10 14H10.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>{loginError}</span>
              </div>
            )}

            {localLoading && (
              <div className="auth-alert auth-alert-info">
                <div className="spinner"></div>
                <span>Signing you in...</span>
              </div>
            )}

            {user && (
              <div className="auth-alert auth-alert-success">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M17 8L8.5 16.5L3 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>✓ Welcome back, {user.name || user.email}!</span>
              </div>
            )}

            {/* Demo Mode Info */}
            {IS_DEMO && (
              <div className="auth-demo-section">
                <div className="auth-alert auth-alert-demo">
                  <span>🎮 Demo Mode - Use buttons below to auto-fill credentials</span>
                </div>
                <div className="auth-demo-buttons">
                  <button 
                    type="button"
                    className="btn-demo"
                    onClick={() => handleDemoLogin('user')}
                  >
                    {DEMO_CREDENTIALS.user.label}
                  </button>
                  <button 
                    type="button"
                    className="btn-demo"
                    onClick={() => handleDemoLogin('admin')}
                  >
                    {DEMO_CREDENTIALS.admin.label}
                  </button>
                </div>
                <div className="auth-divider">
                  <span>or continue with email</span>
                </div>
              </div>
            )}

            {/* Form */}
            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email Address
                </label>
                <div className={`form-input-wrapper ${focusedField === 'email' ? 'focused' : ''}`}>
                  <svg className="form-input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M2 4L9 10L16 4M2 4H16C17.1046 4 18 4.89543 18 6V14C18 15.1046 17.1046 16 16 16H4C2.89543 16 2 15.1046 2 14V4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    className="form-input"
                    disabled={localLoading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <div className={`form-input-wrapper ${focusedField === 'password' ? 'focused' : ''}`}>
                  <svg className="form-input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 9V7C5 4.24 7.24 2 10 2C12.76 2 15 4.24 15 7V9C15.83 9 16.5 9.67 16.5 10.5V18C16.5 19.38 15.38 20.5 14 20.5H6C4.62 20.5 3.5 19.38 3.5 18V10.5C3.5 9.67 4.17 9 5 9ZM7 9H13V7C13 5.34 11.66 4 10 4C8.34 4 7 5.34 7 7V9Z" fill="currentColor"/>
                  </svg>
                  <input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    className="form-input"
                    disabled={localLoading}
                  />
                </div>
              </div>

              <button
                type="submit"
                className="btn-primary-submit"
                disabled={localLoading || !email || !password}
              >
                {localLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            {/* Footer */}
            <div className="auth-footer">
              <p className="auth-footer-text">
                Don't have an account?{' '}
                <button 
                  type="button"
                  className="auth-link"
                  onClick={onNavigateToSignup}
                >
                  Sign Up
                </button>
              </p>
            </div>
          </div>

          {/* Side Decoration */}
          <div className="auth-decoration auth-decoration-1"></div>
          <div className="auth-decoration auth-decoration-2"></div>
        </div>
      </YStack>
    </ScrollView>
  );
};
